import { Theme, TypographyPropsVariantOverrides } from '@mui/material'
import { Components } from '@mui/material/styles'
import { TypographyVariant } from '@mui/material/styles/createTypography'
import { OverridableStringUnion } from '@mui/types'
import { Geist_Mono, Inter } from 'next/font/google'
import { palette } from './palette'

const inter = Inter({
    subsets: ['latin'],
    display: 'swap',
})

export const monoFont = Geist_Mono({
    subsets: ['latin'],
    display: 'swap',
})

declare module '@mui/material/styles' {
    interface TypographyVariants {
        disabled: React.CSSProperties;
        unconfigured: React.CSSProperties;
        eyebrow: React.CSSProperties;
        monospace: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        disabled?: React.CSSProperties;
        unconfigured?: React.CSSProperties;
        eyebrow?: React.CSSProperties;
        monospace?: React.CSSProperties;
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        disabled: true;
        unconfigured: true;
        eyebrow: true;
        monospace: true;
    }
}

export type TypographyVariantType = OverridableStringUnion<TypographyVariant | 'inherit', TypographyPropsVariantOverrides>

export const C1TypographyVariants = {
    fontFamily: inter.style.fontFamily,
    fontSize: 14,
    htmlFontSize: 16,

    // Headings - Inter with semibold/medium, tight letter-spacing
    h1: {
        fontSize: 32, // 2rem
        fontWeight: 600,
        letterSpacing: '-0.025em',
        lineHeight: 1.2,
    },
    h2: {
        fontSize: 24, // 1.5rem
        fontWeight: 600,
        letterSpacing: '-0.02em',
        lineHeight: 1.25,
    },
    h3: {
        fontSize: 20, // 1.25rem
        fontWeight: 550,
        letterSpacing: '-0.015em',
        lineHeight: 1.3,
    },
    h4: {
        fontSize: 18, // 1.125rem
        fontWeight: 500,
        letterSpacing: '-0.01em',
        lineHeight: 1.35,
    },
    h5: {
        fontSize: 16, // 1rem
        fontWeight: 500,
        letterSpacing: '-0.005em',
        lineHeight: 1.4,
    },
    h6: {
        fontSize: 14, // 0.875rem
        fontWeight: 500,
        letterSpacing: '0',
        lineHeight: 1.4,
    },

    // Body text - Inter regular, comfortable line-heights
    body1: {
        fontSize: 14, // 0.875rem
        fontWeight: 450,
        letterSpacing: '0',
        lineHeight: 1.5,
    },
    // body2 is the same as body1 for now
    body2: {
        fontSize: 14, // 0.875rem
        fontWeight: 450,
        letterSpacing: '0',
        lineHeight: 1.5,
    },

    // Subtitles - Medium weight for emphasis
    subtitle1: {
        fontSize: 14, // 0.875rem
        fontWeight: 500,
        letterSpacing: '0.01em',
        lineHeight: 1.4,
    },
    subtitle2: {
        fontSize: 13, // 0.8125rem
        fontWeight: 500,
        letterSpacing: '0.01em',
        lineHeight: 1.4,
    },

    // Caption - Small text for labels
    caption: {
        fontSize: 12, // 0.75rem
        fontWeight: 400,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
        color: palette.text.primary,
    },

    // Overline - All caps, small
    overline: {
        fontSize: 11, // 0.6875rem
        fontWeight: 500,
        letterSpacing: '0.08em',
        lineHeight: 1.5,
        textTransform: 'uppercase',
    },

    // Button text
    button: {
        fontSize: 13, // 0.8125rem
        fontWeight: 500,
        lineHeight: 1.4,
        textTransform: 'none',
    },

    // Custom variants
    eyebrow: {
        fontSize: 11, // 0.6875rem
        fontWeight: 500,
        letterSpacing: '0.1em',
        lineHeight: 1.5,
        textTransform: 'uppercase',
        color: palette.text.secondary,
    },

    monospace: {
        fontFamily: monoFont.style.fontFamily,
        fontSize: 'inherit',
        fontWeight: 500,
        letterSpacing: '-0.02em',
        lineHeight: 1.5,
    },

    disabled: {
        fontSize: 14, // 0.875rem
        fontWeight: 400,
        letterSpacing: '0',
        lineHeight: 1.5,
        opacity: 0.7,
        fontStyle: 'italic',
    },

    unconfigured: {
        fontSize: 14, // 0.875rem
        fontWeight: 400,
        letterSpacing: '0',
        lineHeight: 1.5,
        opacity: 0.7,
        fontStyle: 'italic',
    },
}

export const C1TypographyTheme: Components<Theme>['MuiTypography'] = {
    styleOverrides: {
        root: {
            '& .MuiSvgIcon-root': {
                display: 'inline-block',
                verticalAlign: 'middle',
            },
        },
    },
}
